import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Smartphone,
  Truck,
  LayoutDashboard,
  Building2,
  Briefcase,
  Calendar,
  CreditCard,
  MapPin,
  Bell,
  BarChart3,
  Users,
  Settings,
  Wrench,
} from 'lucide-react';

const Features = () => {
  const featureDetails = [
    {
      id: 'mobile-apps',
      title: 'Mobile Shared Apps (iOS & Android)',
      icon: <Smartphone className="w-12 h-12 text-red-600" />,
      description:
        'Native mobile applications that provide seamless booking experiences for both corporates and individual travelers on iOS and Android platforms.',
      features: [
        'White-labeled apps with your brand identity',
        'Real-time booking and tracking',
        'Secure payment integration',
        'Push notifications for booking updates',
        'Digital receipts and ride history',
        'Multi-language support',
        'Offline mode for essential features',
        'Rating and feedback system',
      ],
    },
    {
      id: 'driver-app',
      title: 'Driver App',
      icon: <Truck className="w-12 h-12 text-red-600" />,
      description:
        'Dedicated driver application that empowers your fleet with seamless communication and efficient job management.',
      features: [
        'Real-time job notifications and assignments',
        'GPS navigation',
        'Digital trip management',
        'Document management and verification',
        'In-app chat support',
        'Driver ratings and reviews',
      ],
    },
    {
      id: 'admin-panel',
      title: 'Admin Panel & Dashboards',
      icon: <LayoutDashboard className="w-12 h-12 text-red-600" />,
      description:
        'Comprehensive admin control center with customizable dashboards for complete operational visibility and management.',
      features: [
        'Real-time fleet tracking on live map',
        'Booking management and dispatching',
        'Driver and vehicle management',
        'Route optimization',
        'Revenue and financial reporting',
        'Customer relationship management',
        'Customizable role-based access',
        'Advanced analytics and insights',
        'Automated report generation',
      ],
    },
    {
      id: 'ets',
      title: 'Employee Transportation Services',
      icon: <Briefcase className="w-12 h-12 text-red-600" />,
      description:
        'Complete enterprise-grade employee transportation management platform designed specifically for corporate clients and large-scale operations.',
      features: [
        'Corporate travel policy enforcement',
        'Multi-level approval workflows',
        'Cost center management',
        'Route optimization',
        'Compliance and compatibility',
        'Travel analytics and reporting',
        'Integration with corporate systems',
        'Centralized billing and invoicing',
      ],
    },
    {
      id: 'vendor-management',
      title: 'Vendor Management',
      icon: <Users className="w-12 h-12 text-red-600" />,
      description:
        'Streamline partnerships and manage your entire vendor network efficiently with comprehensive vendor management tools.',
      features: [
        'Vendor onboarding and verification',
        'Performance tracking and ratings',
        'Contract and agreement management',
        'Automated billing summary',
        'Commission and rate management',
        'Vendor portal for self-service',
        'Compliance monitoring',
        'Multi-vendor comparison tools',
      ],
    },
    {
      id: 'fleet-management',
      title: 'Fleet Management',
      icon: <Wrench className="w-12 h-12 text-red-600" />,
      description:
        'Comprehensive fleet management system to maintain vehicle health, ensure compliance, and manage driver operations efficiently.',
      features: [
        'Vehicle maintenance schedule',
        'Vehicle compliance management',
        'Driver compliance management',
        'Driver allowance and payment management',
        'Vehicle challan/penalty updates',
        'Driver license updates',
      ],
    },
    {
      id: 'booking-solutions',
      title: 'Booking Solutions',
      icon: <Calendar className="w-12 h-12 text-red-600" />,
      description:
        'Advanced booking engine that handles everything from individual bookings to large-scale corporate travel arrangements.',
      features: [
        'Real-time availability and pricing',
        'Instant booking confirmation',
        'Advanced search and filters',
        'Recurring booking templates',
        'Bulk booking capabilities',
        'Automated booking notifications',
        'Flexible cancellation policies',
        'Integration with third-party systems',
      ],
    },
  ];

  const additionalFeatures = [
    {
      icon: <CreditCard className="w-6 h-6 text-red-600" />,
      title: 'Payment Processing',
      description: 'Secure payment gateway integration with multiple payment options and automated invoicing.',
    },
    {
      icon: <MapPin className="w-6 h-6 text-red-600" />,
      title: 'Real-time Tracking',
      description: 'Live GPS tracking for complete visibility of fleet movements and driver locations.',
    },
    {
      icon: <Bell className="w-6 h-6 text-red-600" />,
      title: 'Smart Notifications',
      description: 'Automated alerts and notifications via SMS, email, and push notifications.',
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-red-600" />,
      title: 'Advanced Analytics',
      description: 'Comprehensive reporting and analytics for data-driven decision making.',
    },
    {
      icon: <Building2 className="w-6 h-6 text-red-600" />,
      title: 'Multi-location Support',
      description: 'Manage operations across multiple locations and regions from a single platform.',
    },
    {
      icon: <Settings className="w-6 h-6 text-red-600" />,
      title: 'Customization',
      description: 'Fully customizable platform to match your specific business requirements and workflows.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-white py-20 px-4" data-testid="features-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Powerful Features for Modern Travel Operations</h1>
          <p className="text-xl text-gray-600">
            Discover the comprehensive suite of tools and features that make Frogleap Technologies the ultimate
            platform for travel partners.
          </p>
        </div>
      </section>

      {/* Main Features - Tabbed View */}
      <section className="py-16 bg-white" data-testid="main-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="mobile-apps" className="w-full">
            <div className="overflow-x-auto pb-4">
              <TabsList className="inline-flex w-full min-w-max bg-gray-100 p-1 rounded-lg">
                {featureDetails.map((feature) => (
                  <TabsTrigger
                    key={feature.id}
                    value={feature.id}
                    className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-6 py-3 rounded-md whitespace-nowrap"
                    data-testid={`tab-${feature.id}`}
                  >
                    {feature.title}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {featureDetails.map((feature) => (
              <TabsContent key={feature.id} value={feature.id} className="mt-8" data-testid={`content-${feature.id}`}>
                <Card className="border-none shadow-lg">
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-1">
                        <div className="mb-6">{feature.icon}</div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{feature.title}</h2>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                      <div className="lg:col-span-2">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Capabilities</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {feature.features.map((item, index) => (
                            <div key={index} className="flex items-start space-x-3" data-testid={`capability-${index}`}>
                              <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                                <svg
                                  className="w-3 h-3 text-red-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <p className="text-gray-700 text-sm">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-16 bg-gray-50" data-testid="additional-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Additional Platform Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              More powerful features to supercharge your travel operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalFeatures.map((feature, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow" data-testid={`additional-feature-${index}`}>
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-white" data-testid="integration-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-red-50 to-white p-8 md:p-12 rounded-lg">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Seamless Integration & Scalability</h2>
              <p className="text-gray-600 mb-8">
                Our platform is built with modern technology and architecture, allowing seamless integration with your
                existing systems. Whether you're a small agency or a large enterprise, Frogleap Technologies scales
                with your business needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white px-6 py-3 rounded-lg shadow-sm">
                  <p className="text-sm font-semibold text-gray-700">Cloud-Based</p>
                </div>
                <div className="bg-white px-6 py-3 rounded-lg shadow-sm">
                  <p className="text-sm font-semibold text-gray-700">99.9% Uptime</p>
                </div>
                <div className="bg-white px-6 py-3 rounded-lg shadow-sm">
                  <p className="text-sm font-semibold text-gray-700">Scalable Infrastructure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700 text-white" data-testid="features-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Experience These Features in Action</h2>
          <p className="text-xl mb-8 text-red-100">
            Schedule a personalized demo to see how Frogleap Technologies can transform your operations.
          </p>
          <a href="/contact">
            <button className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors" data-testid="features-demo-btn">
              Schedule a Demo
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Features;
